import MenuIcon from '@mui/icons-material/Menu';
import { IconButton, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { EntityWithIdAndDescription } from '../../types/baseEntities';
import { AsyncSelect } from './AsyncSelect';
import { ControlledTextField } from './ControlledTextField';

interface AsyncSelectOrTextFieldProps<T extends EntityWithIdAndDescription> {
    control: Control<any>;
    name: string;
    label: string;
    loadOptions: () => Promise<T[]>;
    valueField: keyof T;
    descriptionField: keyof T;
    nullOptionLabel?: string;
    onModeChange?: (isFreeSolo: boolean) => void;
    disabled?: boolean;
    buttonType?: 'standard' | 'icon';
    optionPrefix?: string;
    textFieldProps?: Record<string, any>;
}

export const AsyncSelectOrTextField = <T extends EntityWithIdAndDescription,>({
    control,
    name,
    label,
    loadOptions,
    valueField,
    descriptionField,
    nullOptionLabel = 'Otro',
    onModeChange,
    disabled,
    buttonType = 'standard',
    optionPrefix,
    textFieldProps = {}
}: AsyncSelectOrTextFieldProps<T>) => {
    
    // para iniciar en modo select
    const [isFreeSolo, setIsFreeSolo] = useState<boolean>(false);

    // para poner el componente en modo freeSolo si el valor actual no es ninguna de las opciones
    const fieldValue = useWatch({ control, name });
    useEffect(() => {
        if (!fieldValue || fieldValue === 0 || fieldValue === '') return;

        loadOptions().then(allOptions => {
            const valueExists = allOptions.some(
                option => String(option[valueField]) === String(fieldValue)
            );
            
            if (!valueExists) {
                setIsFreeSolo(true);
                if (onModeChange) onModeChange(true);
            }
        });
    }, []);

    if (!isFreeSolo) {
        return (
            /* 2. 
                  React propaga los eventos de los menús flotantes (Portals) hacia sus padres.
                  Si el elemento clickeado pertenece a la opción "-1", se activa el texto libre.
                  La opcion "-1" esta en el ControlledTextField cuando esta en modo Select, es la opcion null
                  */
            <div 
                onClick={(e) => {
                    const target = e.target as HTMLElement;
                    const menuItem = target.closest('[data-value]');
                    if (menuItem && menuItem.getAttribute('data-value') === '-1') {
                        setIsFreeSolo(true);
                        if (onModeChange) onModeChange(true);
                    }
                }}
                style={{ width: '100%' }}
            >
                <AsyncSelect<T>
                    control={control}
                    name={name}
                    label={label}
                    loadOptions={loadOptions}
                    fullWidth
                    nullOptionLabel={nullOptionLabel}
                    valueField={valueField}
                    descriptionField={descriptionField}
                    disabled={disabled}
                    optionPrefix={optionPrefix}
                />
            </div>
        );
    }

    return (
        <Stack direction={'row'} gap={1} alignItems="center" width="100%">
            <ControlledTextField
                name={name}
                label={label}
                control={control}
                size='small'
                autoFocus
                fullWidth
                disabled={disabled}
                {...textFieldProps}
            />
            <IconButton 
                size="small" 
                color="primary"
                disabled={disabled}
                onClick={() => {
                    setIsFreeSolo(false);
                    if (onModeChange) onModeChange(false);
                }}
            >
                <MenuIcon fontSize="small" />
            </IconButton>
        </Stack>
    );
};